import { useState, useCallback } from 'react';
import { useFetch } from './useFetch';
import { API_CONFIG } from '../config/api';

interface Material {
  id: string;
  title: string;
  description: string;
  keywords: string[];
  thumbnailKey: string;
  thumbnailUrl?: string;
  previewUrl: string;
  purchaseUrl: string;
  templateId?: string;
  contentIds: string[];
  contentCount?: number;
  createdAt?: string;
}

interface MyMaterialsState {
  materials: Material[];
  isLoading: boolean;
  error: string | null;
}

export const useMyMaterials = () => {
  const [state, setState] = useState<MyMaterialsState>({
    materials: [],
    isLoading: false,
    error: null,
  });

  const { fetchWithAuth } = useFetch();

  const fetchMyMaterials = useCallback(async (): Promise<void> => {
    setState(prev => ({
      ...prev,
      isLoading: true,
      error: null,
    }));

    try {
      // Usar endpoint específico para materiais do usuário
      const response = await fetchWithAuth(`${API_CONFIG.BASE_URL}/materials/my`);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erro ao buscar meus materiais: ${response.status} - ${errorText}`);
      }

      const data = await response.json();

      // Mapear os campos da API para o formato esperado
      const materials = data.materials?.map((material: any) => ({
        id: material.id,
        title: material.title,
        description: material.description,
        keywords: material.keywords || [],
        thumbnailKey: material.thumbnailKey,
        thumbnailUrl: material.thumbnailUrl,
        previewUrl: material.previewUrl,
        purchaseUrl: material.purchaseUrl,
        templateId: material.templateId,
        contentIds: material.contentIds || [],
        contentCount: material.contents?.length || 0,
        createdAt: material.createdAt,
      })) || [];

      setState(prev => ({
        ...prev,
        materials: materials,
        isLoading: false,
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
        isLoading: false,
      }));
    }
  }, [fetchWithAuth]);

  return {
    materials: state.materials,
    isLoading: state.isLoading,
    error: state.error,
    fetchMyMaterials,
  };
};


